/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { Drawer } from '@mui/material';

import { useBreakpoints } from '../../../hooks/useBreakpoints';

import { useTranslateMessage } from '../../../components/localization';
import { useAppData } from '../../../util/app-data/AppData';
import { AppContext, useAppContext } from '../../../util/app-provider/context';

import { useEntityContext } from '../../../util/entity-provider/context';
import { isProduction } from '../../../util/isProduction';
import { mapChildrenAndUpdatePaths } from './util';
import SidebarContent from './SidebarContent';
import { CDS_SIDEBAR_MOBILE_NAV_WIDTH_PX, CDS_SIDEBAR_RESET_CHROME_EVENT } from './sidebarNavConstants';

const drawerPaperSx = {
  backgroundColor: 'transparent',
  overflow: 'hidden',
  borderRadius: '0 10px 10px 0',
};

const DIVIDER_ITEM = { id: 'divider' };

const Sidebar = ({ pathPrefix }) => {
  const translateMessage = useTranslateMessage();

  const { isXl: isDesktopNavLayout } = useBreakpoints();
  const { isSidebarOpen, toggleSidebar, appRef } = useContext(AppContext);
  const { appConfig } = useAppContext();

  const { entityRef } = useEntityContext();
  const isSmallViewport = !isDesktopNavLayout;

  const { menuConfig: initialMenuConfig } = useAppData();
  const { menuConfig, setCurrentFeature } = useAppContext();

  const location = useLocation();
  const navigate = useNavigate();

  const preparedConfig = useMemo(() => {
    if (!menuConfig) return [];

    const isProd = isProduction();
    const result = [];

    menuConfig.forEach(configItem => {
      if (!configItem || (isProd && configItem.hideInProduction)) return;

      if (configItem.type === 'group-options') {
        const startLen = result.length;

        configItem.children.forEach(groupChild => {
          if (!groupChild.children?.length) {
            result.push(groupChild);

            return;
          }

          result.push({
            ...groupChild,
            children: mapChildrenAndUpdatePaths(groupChild, translateMessage),
          });
        });

        if (result.length > startLen) result.push(DIVIDER_ITEM);

        return;
      }

      if (!configItem.children?.length) {
        result.push(configItem);

        return;
      }

      result.push({
        ...configItem,
        children: mapChildrenAndUpdatePaths(configItem, translateMessage),
      });
    });

    const filtered = result.filter(item => {
      if (!item) return false;

      if (item.id === 'divider') return true;

      if (Array.isArray(item.children) && item.children.length === 0) {
        if (!isProd) {
          // eslint-disable-next-line no-console
          console.warn('[sidebar] Skipping parent nav item with empty submenu', item.id);
        }

        return false;
      }

      return true;
    });

    return filtered.filter((item, index, arr) => {
      if (item.id !== 'divider') return true;
      if (index === 0) return false;
      if (index === arr.length - 1) return false;
      if (arr[index - 1]?.id === 'divider') return false;

      return true;
    });
  }, [menuConfig, translateMessage]);

  const determineCurrentFeature = useCallback(() => {
    const flattenedMenuConfig = preparedConfig.reduce((acc, item) => {
      if (item?.children?.length) {
        return acc.concat(item.children);
      }

      return acc.concat(item);
    }, []);

    const matched = matchPath(
      { path: `${pathPrefix || ''}${appConfig?.isEntitySpecific ? '/:entityRef' : ''}/:path/*` },
      location.pathname
    );

    const targetPath =
      `${matched?.params?.path || ''}${matched?.params?.['*'] ? `/${matched?.params?.['*']}` : ''}` || '';

    const exactMatch = flattenedMenuConfig.find(item => item?.path === `/${targetPath}`);

    if (exactMatch) {
      setCurrentFeature(exactMatch);

      return;
    }

    const dynamicMatch = flattenedMenuConfig.find(
      item => item?.dynamicPath && targetPath?.includes(item?.path?.slice(1))
    );

    setCurrentFeature(dynamicMatch);
  }, [appConfig?.isEntitySpecific, location.pathname, setCurrentFeature, pathPrefix, preparedConfig]);

  useEffect(() => {
    determineCurrentFeature();
  }, [determineCurrentFeature]);

  const handleMenuItemClick = useCallback(
    menuItemPath => {
      const destination = `${pathPrefix || ''}${appConfig?.isEntitySpecific ? `/${entityRef}` : ''}${menuItemPath}`;

      if (destination !== location.pathname) navigate(destination);
    },
    [appConfig?.isEntitySpecific, entityRef, location.pathname, navigate, pathPrefix]
  );

  const amIActive = useCallback(
    me =>
      location.pathname.startsWith(
        `${pathPrefix || ''}${appConfig?.isEntitySpecific ? `/${entityRef}` : ''}${me.path}`
      ),
    [appConfig?.isEntitySpecific, entityRef, location.pathname, pathPrefix]
  );

  const handleDesktopAppIconClick = useCallback(() => {
    window.dispatchEvent(new CustomEvent(CDS_SIDEBAR_RESET_CHROME_EVENT));
  }, []);

  const sharedProps = useMemo(
    () => ({
      handleMenuItemClick,
      toggleSidebar,
      initialMenuConfig,
      preparedConfig,
      amIActive,
      appRef,
    }),
    [handleMenuItemClick, toggleSidebar, initialMenuConfig, preparedConfig, amIActive, appRef]
  );

  if (isSmallViewport) {
    return (
      <Drawer
        open={isSidebarOpen}
        onClose={toggleSidebar}
        sx={{ width: `${CDS_SIDEBAR_MOBILE_NAV_WIDTH_PX}px` }}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <SidebarContent {...sharedProps} isMobile />
      </Drawer>
    );
  }

  return (
    <SidebarContent {...sharedProps} isMobile={false} isDesktopNavLayout onAppIconClick={handleDesktopAppIconClick} />
  );
};

export default Sidebar;
